import { isGoPath } from "@/utils/crossAppMenu";
import { extractLocaleFromPath, toUrlLocale } from "@/utils/locale-url";

/** App()'s decision on its no-prefix locale redirect, for a pathname with no
 * locale segment. */
export interface EntryRedirect {
  isGoRoute: boolean;
  /** The URL to redirect to, or null when App() should render AppContent
   * as-is (a /go/ path, or a URL that already carries a locale). */
  redirectTo: string | null;
}

/**
 * The "no locale prefix in the URL" redirect App() applies, pulled out as a
 * pure function so the /go/ bypass is unit-testable without mounting
 * AppContent's data-fetching hooks (useLayout, useContent, ...) — importing
 * App.tsx itself pulls in modules that use `import.meta.env` (BlockRenderer,
 * ProductDetailContent, the impersonation helpers), which ts-jest cannot
 * parse, so this lives in its own module rather than App.tsx. A /go/... path
 * carries no locale on purpose — the caller cannot know the visitor's — and
 * resolves its own destination via GoShopRoute / GoAccountRoute, so this
 * redirect (and the canonical-casing one right after it in App()) must never
 * fire for it. `storedLanguage` / `detectedLanguage` are resolved by the
 * caller since they read localStorage / navigator.language, which this
 * function does not touch.
 */
export function resolveEntry(
  pathname: string,
  search: string,
  hasStoredLocale: boolean,
  storedLanguage: string,
  detectedLanguage: string
): EntryRedirect {
  const isGoRoute = isGoPath(pathname);
  const urlLocale = extractLocaleFromPath(pathname);

  if (isGoRoute || urlLocale) {
    return { isGoRoute, redirectTo: null };
  }

  // Preserve the path and query string: e.g. password-reset email links
  // arrive without a locale prefix but carry the page slug plus ?token=&email=.
  const preserved = `${pathname === "/" ? "/" : pathname}${search}`;
  const language = hasStoredLocale ? storedLanguage : detectedLanguage;
  return {
    isGoRoute: false,
    redirectTo: `/${toUrlLocale(language)}${preserved}`,
  };
}
